<template>
    <div class="space-y-8 lg:grid lg:grid-cols-12 sm:gap-6 xl:gap-10 lg:space-y-0">
        <!-- Pricing Card -->
        <div class="w-full col-span-3  bg-white rounded-xl">
            <img :src="require('@/assets/images/prices/price1.jpg')"
                class="w-full h-full rounded-xl aspect-video object-cover md:aspect-auto">
        </div>
        <!-- Pricing Card -->
        <div
            class="col-span-6 w-full flex flex-col p-6 mx-auto text-center justify-center text-gray-900 bg-white rounded-xl border border-gray-100 shadow ">
            <div class="justify-center items-baseline mb-6">
                <h3 class="mb-4 text-3xl font-bold">{{ totalQuantity >= 10 ? 'Tarif' : 'À partir de' }}</h3>
                <span class="mr-2 text-5xl font-extrabold">{{ unitPrice }}€</span>
                <span class="text-lg text-gray-600">HT / pièce</span>
            </div>

            <!-- Sélection des tailles -->
            <div class="mb-6">
                <label class="mb-3 block text-sm font-medium text-[#07074D]">
                    Sélection des tailles
                </label>
                <div class="grid grid-cols-1 sm:grid-cols-1 gap-2">
                    <div v-for="size in ['S/M', 'L/XL', '2XL/3XL']" :key="size"
                        class="group relative overflow-hidden rounded-lg transition-all duration-300 bg-white border border-gray-200 hover:border-indigo-300 hover:shadow-sm">
                        <div class="relative p-2.5">
                            <div class="flex items-center justify-between">
                                <div class="flex items-center">
                                    <div
                                        class="w-16 h-9 rounded-lg flex items-center justify-center font-bold text-sm transition-colors bg-indigo-100 text-indigo-600">
                                        {{ size }}
                                    </div>
                                </div>
                                <div class="flex items-center gap-2">
                                    <button type="button" @click="decrementSize(size)" :disabled="sizes[size] === 0"
                                        class="w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200 focus:outline-none focus:ring-1 focus:ring-offset-1"
                                        :class="sizes[size] === 0 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-indigo-100 text-indigo-600 hover:bg-indigo-200 focus:ring-indigo-300'">
                                        <i class="fas fa-minus text-xs"></i>
                                    </button>
                                    <div class="min-w-[2.5rem] text-center text-gray-900">
                                        <div class="text-lg font-bold">{{ sizes[size] }}</div>
                                        <div class="text-[10px] opacity-75 leading-none">pcs</div>
                                    </div>
                                    <button type="button" @click="incrementSize(size)"
                                        class="w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200 focus:outline-none focus:ring-1 focus:ring-offset-1 bg-indigo-100 text-indigo-600 hover:bg-indigo-200 focus:ring-indigo-300">
                                        <i class="fas fa-plus text-xs"></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Alert pour encourager à commander plus -->
            <div v-if="nextTierAlert"
                class="mb-4 p-3 bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-lg shadow-sm">
                <div class="flex items-center gap-2.5">
                    <div class="flex-shrink-0">
                        <div class="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                            <svg class="w-4 h-4 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                    d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                            </svg>
                        </div>
                    </div>
                    <div class="flex-1">
                        <div class="text-[10px] font-semibold text-amber-600 uppercase tracking-wide mb-0.5">Économisez
                            plus</div>
                        <div class="text-xs font-medium text-gray-700">{{ nextTierAlert }}</div>
                    </div>
                </div>
            </div>

            <!-- Total et Tarifs dégressifs -->
            <div class="mb-6">
                <!-- Total Display -->
                <div class="mb-4 p-4 bg-gradient-to-r from-indigo-50 to-indigo-100 rounded-lg border border-indigo-200">
                    <div class="flex items-center justify-between">
                        <div>
                            <div class="text-xs text-gray-600 mb-1">Total sélectionné</div>
                            <div class="text-2xl font-bold"
                                :class="totalQuantity < 10 ? 'text-red-600' : 'text-indigo-600'">
                                {{ totalQuantity }} <span class="text-sm font-normal">pièces</span>
                            </div>
                        </div>
                        <div class="text-right">
                            <div class="text-xs text-gray-600 mb-1">Prix total estimatif</div>
                            <div v-if="totalQuantity >= 10" class="text-2xl font-bold text-indigo-600">
                                {{ totalPrice }}€ <span class="text-sm font-normal">HT</span>
                            </div>
                            <div v-else class="text-xs text-red-600 font-medium">
                                Min. 10 pièces
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Tarifs dégressifs -->
                <div>
                    <h4 class="text-xs font-semibold text-gray-600 mb-2">Tarifs dégressifs</h4>
                    <div class="grid grid-cols-4 gap-2">
                        <div class="p-2.5 rounded-lg border transition-all duration-200"
                            :class="totalQuantity >= 10 && totalQuantity <= 20 ? 'bg-indigo-50 border-indigo-300 shadow-sm' : 'bg-white border-gray-200'">
                            <div class="text-[10px] text-gray-500 mb-0.5">10-20 pcs</div>
                            <div class="text-lg font-bold"
                                :class="totalQuantity >= 10 && totalQuantity <= 20 ? 'text-indigo-600' : 'text-gray-700'">
                                59€<span class="text-xs font-normal">/u</span>
                            </div>
                        </div>

                        <div class="p-2.5 rounded-lg border transition-all duration-200"
                            :class="totalQuantity >= 21 && totalQuantity <= 49 ? 'bg-indigo-50 border-indigo-300 shadow-sm' : 'bg-white border-gray-200'">
                            <div class="text-[10px] text-gray-500 mb-0.5">21-49 pcs</div>
                            <div class="text-lg font-bold"
                                :class="totalQuantity >= 21 && totalQuantity <= 49 ? 'text-indigo-600' : 'text-gray-700'">
                                55€<span class="text-xs font-normal">/u</span>
                            </div>
                        </div>

                        <div class="p-2.5 rounded-lg border transition-all duration-200"
                            :class="totalQuantity >= 50 && totalQuantity <= 99 ? 'bg-indigo-50 border-indigo-300 shadow-sm' : 'bg-white border-gray-200'">
                            <div class="text-[10px] text-gray-500 mb-0.5">50-99 pcs</div>
                            <div class="text-lg font-bold"
                                :class="totalQuantity >= 50 && totalQuantity <= 99 ? 'text-indigo-600' : 'text-gray-700'">
                                53€<span class="text-xs font-normal">/u</span>
                            </div>
                        </div>

                        <div class="p-2.5 rounded-lg border transition-all duration-200"
                            :class="totalQuantity >= 100 ? 'bg-indigo-50 border-indigo-300 shadow-sm' : 'bg-white border-gray-200'">
                            <div class="text-[10px] text-gray-500 mb-0.5">100+ pcs</div>
                            <div class="text-lg font-bold"
                                :class="totalQuantity >= 100 ? 'text-indigo-600' : 'text-gray-700'">
                                49€<span class="text-xs font-normal">/u</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Button textButton="Demander un devis" dark @click="emitReservationEvent"></Button>
        </div>
        <!-- Pricing Card -->
        <div class="w-full col-span-3 bg-white rounded-xl">
            <img :src="require('@/assets/images/prices/price2.jpg')" class="w-full h-full rounded-xl object-cover">
        </div>
    </div>
</template>

<script>
import Button from '@/Components/Buttons/Button.vue';

export default {
    name: 'PricingCards',
    components: {
        Button,
    },
    data() {
        return {
            sizes: {
                'S/M': 0,
                'L/XL': 0,
                '2XL/3XL': 0
            }
        };
    },

    computed: {
        totalQuantity() {
            return Object.values(this.sizes).reduce((sum, qty) => sum + qty, 0);
        },

        unitPrice() {
            const qty = this.totalQuantity;
            if (qty < 10) return 59;
            if (qty >= 10 && qty <= 20) return 59;
            if (qty >= 21 && qty <= 49) return 55;
            if (qty >= 50 && qty <= 99) return 53;
            return 49; // 100+
        },

        totalPrice() {
            return this.totalQuantity * this.unitPrice;
        },

        nextTierAlert() {
            const qty = this.totalQuantity;
            const basePrice = 59;

            if (qty < 10) {
                return null;
            }

            if (qty >= 10 && qty <= 20) {
                const piecesNeeded = 21 - qty;
                const currentSavings = (basePrice - 59) * qty;
                return `Vous bénéficiez de ${currentSavings}€ de réduction. Le prochain palier nécessite ${piecesNeeded} pièce${piecesNeeded > 1 ? 's' : ''} pour avoir la pièce à 55€ HT/u au lieu de 59€ HT/u`;
            }

            if (qty >= 21 && qty <= 49) {
                const piecesNeeded = 50 - qty;
                const currentSavings = (basePrice - 55) * qty;
                return `Vous bénéficiez de ${currentSavings}€ de réduction. Le prochain palier nécessite ${piecesNeeded} pièce${piecesNeeded > 1 ? 's' : ''} pour avoir la pièce à 53€ HT/u au lieu de 59€ HT/u`;
            }

            if (qty >= 50 && qty <= 99) {
                const piecesNeeded = 100 - qty;
                const currentSavings = (basePrice - 53) * qty;
                return `Vous bénéficiez de ${currentSavings}€ de réduction. Le prochain palier nécessite ${piecesNeeded} pièce${piecesNeeded > 1 ? 's' : ''} pour avoir la pièce à 49€ HT/u au lieu de 59€ HT/u`;
            }

            if (qty >= 100) {
                const currentSavings = (basePrice - 49) * qty;
                return `Bravo ! Vous bénéficiez de ${currentSavings}€ de réduction avec le meilleur tarif !`;
            }

            return null;
        }
    },

    watch: {
        sizes: {
            handler(newSizes) {
                // Émettre les changements de tailles en temps réel
                this.$emit('sizes-changed', { sizes: newSizes });
            },
            deep: true
        }
    },

    methods: {
        incrementSize(size) {
            this.sizes[size]++;
        },

        decrementSize(size) {
            if (this.sizes[size] > 0) {
                this.sizes[size]--;
            }
        },

        emitReservationEvent() {
            this.$emit('reservation', { sizes: this.sizes });
        }
    }
}
</script>
